import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Star } from "lucide-react";
import { formatDateDE } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Bewertung() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/reviews/by-token/${token}`);
        setData(r.data);
        if (r.data?.already_submitted) {
          setRating(r.data.rating);
          setText(r.data.text);
          setSubmitted(true);
        }
      } catch {
        setError("Bewertungslink ungültig oder abgelaufen.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews/by-token/${token}`, { rating, text });
      setSubmitted(true);
      toast.success("Vielen Dank für Ihre Bewertung!");
    } catch {
      toast.error("Fehler beim Senden");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center"><Loader2 className="animate-spin text-[#BA6D52]" size={32} /></div>;
  if (error) return <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-6"><div className="max-w-md text-center"><h1 className="font-serif text-3xl">{error}</h1></div></div>;

  return (
    <div data-testid="page-bewertung" className="min-h-screen bg-[#FAFAF7] py-16 md:py-24">
      <div className="max-w-2xl mx-auto px-6 md:px-12">
        <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52]">Lukas Stutz — Mietkoch</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-4">Ihre Bewertung</h1>
        <p className="text-[#757067] mt-3">Einsatz bei <b>{data.betrieb}</b> am {formatDateDE(data.einsatzdatum)}</p>

        {submitted ? (
          <div className="mt-12 bg-emerald-50 border border-emerald-200 p-8 text-center">
            <CheckCircle2 size={48} className="mx-auto text-emerald-600" />
            <h2 className="font-serif text-2xl mt-4 text-emerald-900">Vielen Dank!</h2>
            <p className="text-sm text-emerald-800 mt-2">Ihre Bewertung wurde gespeichert.</p>
            <div className="flex justify-center mt-4 gap-1">{[1,2,3,4,5].map((s) => <Star key={s} size={24} className={s <= rating ? "text-[#BA6D52] fill-[#BA6D52]" : "text-[#E3DECE]"} />)}</div>
            {text && <p className="text-sm text-[#757067] mt-4 italic">&bdquo;{text}&ldquo;</p>}
          </div>
        ) : (
          <div className="mt-12 bg-white border border-[#E3DECE] p-6 md:p-8">
            <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52]">Wie zufrieden waren Sie?</p>
            <div className="flex gap-2 mt-4">
              {[1,2,3,4,5].map((s) => (
                <button
                  key={s}
                  type="button"
                  onMouseEnter={() => setHoverRating(s)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(s)}
                  data-testid={`rate-${s}`}
                  className="transition-transform hover:scale-110"
                >
                  <Star size={40} className={s <= (hoverRating || rating) ? "text-[#BA6D52] fill-[#BA6D52]" : "text-[#E3DECE]"} />
                </button>
              ))}
            </div>

            <div className="mt-8">
              <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Ihr Kommentar (optional)</label>
              <textarea
                rows={5}
                value={text}
                onChange={(e) => setText(e.target.value)}
                data-testid="bewertung-text"
                placeholder="Was hat Ihnen besonders gefallen? Gibt es Verbesserungsvorschläge?"
                className="mt-2 w-full bg-[#FAFAF7] border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] p-3 text-sm font-light"
              />
            </div>

            <button
              onClick={submit}
              disabled={submitting}
              data-testid="bewertung-submit"
              className="mt-6 bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-8 py-4 text-xs uppercase tracking-[0.2em]"
            >
              {submitting ? "Wird gesendet…" : "Bewertung absenden"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
